import { makeService } from '@upstart/shared-api-utils';
import { OkResponse } from 'util/api';
import { useTypedBackendClient } from './useTypedBackendClient';

export type GetLendingSupportDealFinanceOfferResponse = OkResponse<
  '/v2/workflow/lending-support/deal/{dealId}/finance-offer',
  'get'
>['data'];

export type CrmMergeLeadsOperation = OkResponse<
  '/v2/workflow/lending-support/deal/{dealId}/crm-merge-leads-operations',
  'get'
>['data'][number];

export interface LendingSupportService {
  getDealFinanceOffer(args: {
    dealId: string;
  }): Promise<GetLendingSupportDealFinanceOfferResponse>;
  getDealCrmMergeLeadsOperations(args: {
    dealId: string;
  }): Promise<CrmMergeLeadsOperation[]>;
}

const useLendingSupportFns = (): LendingSupportService => {
  const { backendClient } = useTypedBackendClient();

  const getDealFinanceOffer: LendingSupportService['getDealFinanceOffer'] =
    async ({ dealId }) => {
      const { data, error } = await backendClient.GET(
        '/v2/workflow/lending-support/deal/{dealId}/finance-offer',
        {
          params: {
            path: { dealId },
          },
        },
      );
      if (error) {
        throw new Error(error.detail || error.title);
      }
      return data.data;
    };

  const getDealCrmMergeLeadsOperations: LendingSupportService['getDealCrmMergeLeadsOperations'] =
    async ({ dealId }) => {
      const { data, error } = await backendClient.GET(
        '/v2/workflow/lending-support/deal/{dealId}/crm-merge-leads-operations',
        {
          params: {
            path: { dealId },
          },
        },
      );
      if (error) {
        throw new Error(error.detail || error.title);
      }
      return data.data;
    };

  return {
    getDealFinanceOffer,
    getDealCrmMergeLeadsOperations,
  };
};

const {
  OverridesProvider: LendingSupportServiceOverridesProvider,
  useService: useLendingSupportService,
} = makeService({
  name: 'LendingSupport',
  useServiceFns: useLendingSupportFns,
});

export { LendingSupportServiceOverridesProvider, useLendingSupportService };
